import {
  useCapTableTransactions,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import { Button, MultiSelect, Switch } from '@commenda/kakashi';
import {
  DirectorBase,
  TEquityInfoKS,
} from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { CorporationType } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import {
  filterShareholders,
  omitLegacyOwnershipFlags,
  validateEquityInfo,
} from '../../common/helpers';
import { getDirectorName } from '../Director/common';
import DirectorModal from '../Director/new/DirectorModal';
import CapTableKS from '../EquityInfo/CapTable';
import CompanyValuationInputKS from '../EquityInfo/CompanyValuationInputKS';
import EquitySplitInputKS from '../EquityInfo/EquitySplitInputKS';
import EsopPoolInputKS from '../EquityInfo/EsopPoolInputKS';
import { TDirectorIdToSharePercentageMap } from '../EquityInfo/types';
import { IncorporationFormHeading } from '../common';
import DirectorForm from './USDirectorForm';
import { validateDirectorInfo } from './validators';


const EquityInfo = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  companyId,
  corporationId,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
  companyId: number;
  corporationId: number;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const { data: directorsData, mutate: mutateDirectors } =
    useGetAllDirectorsInCompany(companyId);

  const { data: capTableTransactions } = useCapTableTransactions(corporationId);

  const shareholderDirectorIds = useMemo(
    () =>
      new Set(
        capTableTransactions?.map(
          (tx) => tx.stakeholding?.stakeholder?.director?.id
        )
      ),
    [capTableTransactions]
  );

  const [isDirectorModalShown, setDirectorModalShown] = useState(false);
  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });

  const [hasESOPs, setHasESOPs] = useState<boolean>(false);

  const [isSubmitting, setSubmitting] = useState(false);

  // isShareholder will be used to find who is a shareholder.
  // It includes all the shareholders you have added.
  // If an id is found in directorVsSharePercentage, it means it was added to the equity split at some point.
  // If id exists and isShareholder is true that means they are getting displayed.
  // If id exists and isShareholder is false, that means they were added and now they have been removed.
  // The reason why we are not checking shareholders by just sharePercentage is because there can be a shareholder with 0%.
  const [directorVsSharePercentage, setDirectorVsSharePercentage] =
    useState<TDirectorIdToSharePercentageMap>();

  const [editDirector, setEditDirector] = useState<DirectorBase>();

  const [shareholders, setShareholders] = useState<DirectorBase[]>([]);

  const [tableData, setTableData] = useState<
    {
      id: number;
      name: string;
      ownership: string;
      actualOwnership: string;
      esopContribution: string;
      shares: string;
    }[]
  >([]);

  const afterAddShareholder = (dir) => {
    const directorVsSharePercentageCopy = { ...directorVsSharePercentage };

    if (dir.id in directorVsSharePercentageCopy) {
      directorVsSharePercentageCopy[dir.id] = {
        ...directorVsSharePercentageCopy[dir.id],
        name: getDirectorName(dir),
      };
    } else {
      directorVsSharePercentageCopy[dir.id] = {
        name: getDirectorName(dir),
        sharePercentage: 0,
        id: dir.id,
        shareCount: 0,
        isShareholder: true,
      };
    }
    setDirectorVsSharePercentage(directorVsSharePercentageCopy);
    setDirectorIdsWithError((prevIds) => {
      const newIds = new Set(prevIds);
      newIds.delete(dir.id);
      return newIds;
    });
  };

  const onEditShareholderDetais = (dirId) => {
    const selectedDirector = directorsData?.directors?.find(
      (dir) => dir.id === dirId
    );
    if (selectedDirector) {
      setEditDirector(selectedDirector);
      setDirectorModalShown(true);
    }
  };

  const onChangeShareholder = (val: string[]) => {
    const directorVsSharePercentageCopy = { ...directorVsSharePercentage };
    let allDirectorVSMapIds = Object.keys(directorVsSharePercentageCopy || {});

    // If we are adding a new shareholder from dropdown add it to the directorVsSharePercentage
    val.forEach((k) => {
      const dir = directorsData?.directors?.find(
        (d) => d.id === parseInt(k, 10)
      );
      if (!allDirectorVSMapIds.includes(k)) {
        directorVsSharePercentageCopy[k] = {
          name: getDirectorName(dir),
          sharePercentage: 0,
          id: dir.id,
          shareCount: 0,
          isShareholder: true,
        };
      } else if (!directorVsSharePercentageCopy[k]?.isShareholder) {
        directorVsSharePercentageCopy[k] = {
          name: getDirectorName(dir),
          sharePercentage: 0,
          id: dir.id,
          shareCount: 0,
          isShareholder: true,
        };
      }
    });

    allDirectorVSMapIds = Object.keys(directorVsSharePercentageCopy || {});

    // if we remove anything from dropdown, update it from the directorVsSharePercentage
    allDirectorVSMapIds.forEach((k) => {
      if (!val.includes(k)) {
        directorVsSharePercentageCopy[k] = {
          ...directorVsSharePercentageCopy[k],
          sharePercentage: 0,
          shareCount: 0,
          isShareholder: false,
        };
        if (directorIdsWithError.has(parseInt(k, 10))) {
          setDirectorIdsWithError((prevIds) => {
            const newIds = new Set(prevIds);
            newIds.delete(parseInt(k, 10));
            return newIds;
          });
        }
      }
    });

    setDirectorVsSharePercentage(directorVsSharePercentageCopy);
  };

  const generateDirectorPayload = () => {
    const shareHolderArr = Object.values(directorVsSharePercentage || {}).map(
      (dsp) => {
        const d = directorsData?.directors?.find((dr) => dr.id === dsp.id);
        let corporationDirector;
        if (d) {
          corporationDirector = d.corporationHasDirector.find(
            (chd) =>
              chd.corporationId === corporationId && chd.directorId === d.id
          );
        }
        if (corporationDirector) {
          return {
            id: dsp.id,
            corporationHasDirector: [
              {
                corporationId,
                directorId: dsp.id,
                additionalDirectorInformation: {
                  ...omitLegacyOwnershipFlags(
                    corporationDirector.additionalDirectorInformation
                  ),
                  sharePercentage: dsp.sharePercentage,
                },
              },
            ],
          };
        }
        return {
          id: dsp.id,
          corporationHasDirector: [
            {
              corporationId,
              directorId: dsp.id,
              additionalDirectorInformation: {
                sharePercentage: dsp.sharePercentage,
              },
            },
          ],
        };
      }
    );

    return shareHolderArr;
  };

  const getCapTableDataUS = () => {
    const esopPool = equityInfo.shareRetained || 0;
    const ownershipPool = 100 - esopPool;
    let totalOwnership = 0;
    let totalActualOwnership = 0;
    let totalShares = 0;
    const td = Object.values(directorVsSharePercentage || {})
      .filter((dsp) => dsp.isShareholder)
      .map((dir) => {
        const actualOwnership =
          hasESOPs &&
            data?.requestData?.corporationType === CorporationType.CCorp
            ? (ownershipPool * dir.sharePercentage) / 100
            : dir.sharePercentage;
        totalOwnership += dir.sharePercentage || 0;
        totalActualOwnership += actualOwnership;
        totalShares += Math.round(
          (equityInfo.shareCount * actualOwnership) / 100
        );
        return {
          id: dir.id,
          name: dir.name,
          ownership: dir.sharePercentage ? dir.sharePercentage.toFixed(2) : '',
          actualOwnership: actualOwnership ? actualOwnership.toFixed(2) : '',
          esopContribution: '',
          shares: Math.round(
            (equityInfo.shareCount * actualOwnership) / 100
          ).toLocaleString(),
        };
      });
    if (
      hasESOPs &&
      data?.requestData?.corporationType === CorporationType.CCorp
    ) {
      td.push({
        id: -1,
        name: 'ESOP Shares',
        ownership: '',
        actualOwnership: '',
        esopContribution: esopPool.toFixed(2),
        shares: Math.round(
          (equityInfo.shareCount * esopPool) / 100
        ).toLocaleString(),
      });
    }

    td.push({
      id: -2,
      name: 'Total',
      ownership: totalOwnership.toFixed(2),
      actualOwnership: totalActualOwnership.toFixed(2),
      esopContribution: esopPool.toFixed(2),
      shares: (
        totalShares +
        (hasESOPs ? Math.round((equityInfo.shareCount * esopPool) / 100) : 0)
      ).toLocaleString(),
    });
    setTableData(td);
  };

  const onSubmit = async () => {
    setSubmitting(true);

    const validationResult = validateEquityInfo(
      equityInfo,
      [],
      directorVsSharePercentage,
      undefined,
      true
    );

    if (validationResult) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: validationResult.message,
      });
      setSubmitting(false);
      return;
    }

    const formError = validateDirectorInfo(
      directorsData?.directors,
      Object.values(directorVsSharePercentage)
        .filter((dsp) => dsp.isShareholder)
        .map((dsp) => dsp.id)
    );

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));
      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for partners by clicking on "Edit".`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const body = {
      requestData: {
        shareCount: parseInt(equityInfo.shareCount?.toString(), 10),
        sharePrice: parseFloat(equityInfo.price?.toString()),
        shareRetained: hasESOPs
          ? parseFloat(equityInfo.shareRetained?.toString())
          : 0,
      },
      directors: generateDirectorPayload(),
    };

    const response = await fetcher(
      `workRequest/${id}`,
      { method: 'POST', body },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      return;
    }

    await mutate();
    next();
    setSubmitting(false);
  };

  useEffect(() => {
    setShareholders(
      data?.directors?.filter((director) =>
        director.corporationHasDirector?.some((directorWithCorporation) =>
          filterShareholders(
            directorWithCorporation,
            director.id,
            data.corporationId,
            shareholderDirectorIds
          )
        )
      )
    );

    setEquityInfo({
      price: data?.requestData?.sharePrice || 0.00001,
      shareCount: data?.requestData?.shareCount || 10000000,
      shareRetained: data?.requestData?.shareRetained || 15,
    });
    setHasESOPs(data?.requestData?.shareRetained || false);
  }, [data, shareholderDirectorIds]);

  useEffect(() => {
    const directorVsEquityMap: TDirectorIdToSharePercentageMap = {};

    shareholders.sort().forEach((director) => {
      const details = director.corporationHasDirector.find(
        (directorWithCorporation) =>
          filterShareholders(
            directorWithCorporation,
            director.id,
            data?.corporationId,
            shareholderDirectorIds
          )
      ).additionalDirectorInformation;
      if (details)
        directorVsEquityMap[director.id] = {
          name: getDirectorName(director),
          sharePercentage: details.sharePercentage || 0,
          id: director.id,
          shareCount: details?.shareCount || 0,
          isDirector: details.isDirector,
          isShareholder: true,
        };
    });

    setDirectorVsSharePercentage((prev) => ({
      ...prev,
      ...directorVsEquityMap,
    }));
  }, [shareholders]);

  useEffect(() => {
    if (!isDirectorModalShown) {
      setEditDirector(undefined);
    }
  }, [isDirectorModalShown]);

  useEffect(() => {
    getCapTableDataUS();
  }, [directorVsSharePercentage, equityInfo, hasESOPs]);

  return (
    <div className="flex flex-col gap-10 max-w-[700px]">
      <IncorporationFormHeading
        text={
          data?.requestData?.corporationType === CorporationType.LLC
            ? 'Allocate company ownership'
            : 'Company Ownership'
        }
        additionalText={
          data?.requestData?.corporationType === CorporationType.LLC
            ? 'Please provide information about this company’s members.'
            : "Please provide information about this corporation's shareholders."
        }
      />

      <div className="">
        {data?.requestData?.corporationType === CorporationType.LLC ? (
          <CompanyValuationInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
            shareCountPlaceholder="10,000,000"
            sharePricePlaceholder="0.00001"
            currency={{
              countryCode: 'en-US',
              name: 'USD',
              style: '$',
            }}
            helpTexts={{
              mainHeading: 'Capital contributions',
              bodyText:
                'We suggest you keep the default values as they are standard for LLC in the United States.',
              connectingText: 'capital contributions valued at',
            }}
            isNumberOfSharesApplicable={false}
          />
        ) : (
          <CompanyValuationInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
            shareCountPlaceholder="10,000,000"
            sharePricePlaceholder="0.00001"
            currency={{
              countryCode: 'en-US',
              name: 'USD',
              style: '$',
            }}
            helpTexts={{
              mainHeading: 'Authorized Shares',
              bodyText:
                'We suggest you keep the default values as they are standard for corporations in the United States.',
              connectingText: 'shares valued at',
            }}
          />
        )}
      </div>

      <div className=" flex flex-col gap-4">
        {data?.requestData?.corporationType === CorporationType.LLC ? (
          <div>
            <h3 className="text-base">Membership Interest Allocation</h3>
            <span className="text-sm text-neutral-2">
              Total allocation must add up to 100%.
            </span>
          </div>
        ) : (
          <div>
            <h3 className="text-base">Shareholder Equity</h3>
          </div>
        )}
        {data?.requestData?.corporationType === CorporationType.LLC ? null : (
          <div className="flex gap-2">
            <Switch
              checked={hasESOPs}
              defaultChecked={hasESOPs}
              onCheckedChange={(val: boolean) => {
                if (!val) {
                  setHasESOPs(false);
                  setEquityInfo((prev) => ({ ...prev, shareRetained: 0 }));
                  return;
                }
                setHasESOPs(true);
              }}
            />
            <span>My company has an employee stock option pool (ESOP)</span>
          </div>
        )}
      </div>
      {hasESOPs &&
        data?.requestData?.corporationType === CorporationType.CCorp ? (
        <div className=" flex flex-col gap-4">
          <EsopPoolInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
          />
        </div>
      ) : null}

      <div className=" flex flex-col gap-4 pb-12 border-b">
        {hasESOPs &&
          data?.requestData?.corporationType === CorporationType.CCorp ? (
          <div>
            <h3 className="text-base">Ownership Pool</h3>
          </div>
        ) : null}
        <MultiSelect
          id="shareholder-multiselect"
          options={
            directorsData?.directors?.map((dir) => {
              return {
                label: getDirectorName(dir),
                value: String(dir.id),
              };
            }) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder:
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add a shareholder'
                : 'Add a member',
            onAddItem: () => setDirectorModalShown(true),
          }}
          defaultValue={Object.values(directorVsSharePercentage || {})
            .filter((dsp) => dsp.isShareholder)
            .map((dsp) => dsp.id?.toString())}
        />
        <EquitySplitInputKS
          directorVsSharePercentage={directorVsSharePercentage}
          setDirectorVsSharePercentage={setDirectorVsSharePercentage}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetais}
          directorIdsWithError={Array.from(directorIdsWithError)}
        />
      </div>

      <div className="flex flex-col pt-2 gap-4">
        {data?.requestData?.corporationType === CorporationType.CCorp ? (
          <h3 className="text-base">Ownership Summary</h3>
        ) : null}
        <CapTableKS
          data={tableData}
          showEsopPool={
            hasESOPs &&
            data?.requestData?.corporationType === CorporationType.CCorp
          }
          isNumberOfSharesApplicable={
            data?.requestData?.corporationType === CorporationType.CCorp
          }
        />
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button onClick={onSubmit} disabled={isSubmitting}>
          <span>Proceed</span>
          <ProceedIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <DirectorModal
        isOpen={isDirectorModalShown}
        director={editDirector}
        setOpen={setDirectorModalShown}
        companyDataMutate={mutateDirectors}
        DirectorForm={DirectorForm}
        setNotification={setNotification}
        companyId={companyId}
        corporationId={corporationId}
        token={token}
        workRequestData={data}
        modalConfig={{
          heading:
            data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Add a shareholder'
              : 'Add a member',
          postSave: afterAddShareholder,
        }}
      />
    </div>
  );
};

export default EquityInfo;
